/*
 * niimbot-cart.js
 *
 * Single client-side store for the cart drawer and cart page. Uses
 * optimistic UI: quantity / remove changes update the DOM immediately
 * from in-memory state, then a debounced /cart/update.js syncs the
 * server. Avoids Dawn's section-rendering round trip entirely.
 */
(function () {
  if (window.__nbCartInit) return;
  window.__nbCartInit = true;

  var CART_GET = '/cart.js';
  var CART_UPDATE = '/cart/update.js';
  var CART_ADD = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';

  var state = {
    cart: null,
    syncTimer: null,
    syncing: false,
    pendingResync: false,
    syncFailed: false,
    checkoutPending: false,
    isCustomer: false,
    desiredByKey: {}, // last user-requested quantity per item key (for stock-cap detection)
  };

  // ---------- helpers ----------
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var dollars = cents / 100;
    return dollars % 1 === 0 ? 'S$' + dollars.toFixed(0) : 'S$' + dollars.toFixed(2);
  }

  // Parse a displayed money string ("S$37.60", "S$1,234.00") back to integer
  // cents. Used to read the YMQ-rewritten member line totals out of the DOM.
  function parseMoneyToCents(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (!cleaned) return null;
    var value = parseFloat(cleaned);
    if (isNaN(value)) return null;
    return Math.round(value * 100);
  }

  // Build a price <span> carrying the YMQ B2B markers the plugin scans for.
  // The whole member-pricing logic (enterprise 15%, early-bird 20%, quantity
  // breaks for ordinary customers, etc.) lives inside the YMQ app, NOT here:
  // Shopify's cart JSON always holds the ORIGINAL price, and YMQ rewrites
  // these marked spans to the customer-specific price on the client. We just
  // emit the markers exactly the way Dawn's default cart does so YMQ treats
  // our custom cart identically. `marker` is the YMQ attribute name
  // (ymq-b2b-item-original-price / -final-price / -original-line-price /
  // -final-line-price). The ymq-b2b-price-hidden class is only applied for
  // logged-in customers (YMQ reveals it once it has computed their price);
  // guests get a plain, immediately-visible original price.
  function ymqItemSpan(keyAttr, marker, cents) {
    var cls = state.isCustomer ? 'ymq-b2b-price-hidden' : '';
    return '<span class="' + cls + '" ymq-b2b-item-key="' + escapeHtml(keyAttr) +
           '" ' + marker + '>' + formatMoney(cents) + '</span>';
  }

  function buildImageUrl(url, width) {
    if (!url) return '';
    var base = url.split('?')[0];
    base = base.replace(/_(small|medium|large|grande|\d+x\d+|\d+x)\./, '.');
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // ---------- selection (which items to checkout) ----------
  // Persisted per-session as a set of UNCHECKED line keys. Absence = checked.
  var SELECTION_KEY = 'nb_cart_unchecked';
  function readUnchecked() {
    try {
      var raw = sessionStorage.getItem(SELECTION_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  }
  function writeUnchecked(arr) {
    try { sessionStorage.setItem(SELECTION_KEY, JSON.stringify(arr)); } catch (_) {}
  }
  function isChecked(key) {
    return readUnchecked().indexOf(key) === -1;
  }
  function setChecked(key, checked) {
    var list = readUnchecked();
    var idx = list.indexOf(key);
    if (checked && idx !== -1) list.splice(idx, 1);
    else if (!checked && idx === -1) list.push(key);
    writeUnchecked(list);
  }
  // Prune unchecked entries for keys that no longer exist (after removal).
  function pruneUnchecked(currentKeys) {
    var list = readUnchecked();
    var pruned = list.filter(function (k) { return currentKeys.indexOf(k) !== -1; });
    if (pruned.length !== list.length) writeUnchecked(pruned);
  }

  function fetchCart() {
    return fetch(CART_GET, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; return cart; });
  }

  // Items with quantity > 0 (filter defensive against phantom 0-qty lines).
  function visibleItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (i) { return i && i.quantity > 0; });
  }

  // ---------- rendering ----------
  function renderAll() {
    document.querySelectorAll('[data-nb-cart]').forEach(renderInto);
    document.dispatchEvent(new CustomEvent('niimbot:cart:updated', { detail: { cart: state.cart } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state.cart } }));
  }

  function renderInto(root) {
    var cart = state.cart;
    if (!cart) return;

    var items = visibleItems(cart);
    var itemCount = items.reduce(function (n, i) { return n + i.quantity; }, 0);
    // Prune any orphaned unchecked entries before computing the selected total.
    pruneUnchecked(items.map(function (i) { return i.key; }));
    // Compute subtotal from unit price * quantity, ONLY for selected items.
    // Never trust a possibly stale final_line_price (it can briefly be 0
    // right after a server-side recompute, or undefined for items returned
    // by a partial /cart/add).
    var selectedCount = 0;
    var totalCents = items.reduce(function (n, i) {
      if (!isChecked(i.key)) return n;
      var unit = (typeof i.final_price === 'number' ? i.final_price
                : typeof i.price === 'number' ? i.price : 0);
      selectedCount += i.quantity || 0;
      return n + unit * (i.quantity || 0);
    }, 0);

    var target = root.getAttribute('data-nb-cart-target');
    var itemRenderer = (target === 'page') ? renderItemPageRow : renderItem;
    var bodyEl = root.querySelector('.nb-cartpage__body');
    var itemsEl = root.querySelector('[data-nb-cart-items]');
    var emptyEl = root.querySelector('[data-nb-cart-empty]');
    var totalsEl = root.querySelector('[data-nb-cart-totals]');
    var totalValEl = root.querySelector('[data-nb-cart-total]');
    var countEls = root.querySelectorAll('[data-nb-cart-count]');
    var checkoutBtn = root.querySelector('[data-nb-cart-checkout]');

    var isEmpty = itemCount === 0;
    if (emptyEl) emptyEl.hidden = !isEmpty;
    if (itemsEl) itemsEl.hidden = isEmpty;
    if (totalsEl) totalsEl.hidden = isEmpty;
    if (bodyEl) bodyEl.hidden = isEmpty;

    if (!isEmpty && itemsEl) {
      itemsEl.innerHTML = items.map(function (item, i) { return itemRenderer(item, i + 1); }).join('');
    } else if (isEmpty && itemsEl) {
      itemsEl.innerHTML = '';
    }

    if (totalValEl) totalValEl.innerHTML = renderTotal(totalCents);

    // Members: the YMQ B2B app rewrites per-line prices to member prices
    // after this render, but not our self-owned subtotal. Reconcile the
    // subtotal against the displayed line totals now (in case YMQ has
    // already run) and keep it in sync on every later YMQ mutation.
    if (state.isCustomer && !isEmpty) {
      reconcileMemberSubtotal(root, totalValEl, totalCents);
      watchMemberPrices(root, itemsEl, totalValEl);
    }

    countEls.forEach(function (el) { el.textContent = itemCount; });
    if (checkoutBtn) {
      var noneSelected = selectedCount === 0;
      if (isEmpty || noneSelected || state.checkoutPending) { checkoutBtn.setAttribute('aria-disabled', 'true'); checkoutBtn.classList.add('is-disabled'); }
      else { checkoutBtn.removeAttribute('aria-disabled'); checkoutBtn.classList.remove('is-disabled'); }
      // Surface how many items will be checked out so the merchant copy is honest.
      var ctaLabel = checkoutBtn.querySelector('[data-nb-checkout-label]');
      if (ctaLabel) {
        ctaLabel.textContent = (noneSelected || selectedCount === itemCount)
          ? 'Check out'
          : 'Check out (' + selectedCount + ')';
      }
    }
  }

  function renderItem(item, line) {
    var img = buildImageUrl(item.image, 240);
    var variantBits = [];
    if (!item.product_has_only_default_variant && item.options_with_values) {
      item.options_with_values.forEach(function (o) { variantBits.push(o.name + ': ' + o.value); });
    }
    var variantText = variantBits.join(' · ');

    var itemKey = item.key || '';
    var idx = line - 1;
    var unitOriginal = item.original_price || item.price || 0;
    var unitFinal = item.final_price || item.price || 0;
    var compareCents = unitOriginal * item.quantity;
    var finalCents = unitFinal * item.quantity;
    // Only treat as "on sale" when there's a real per-unit discount (avoids
    // showing a stale strikethrough during optimistic recompute).
    var lineOnSale = unitOriginal > 0 && unitFinal > 0 && unitOriginal !== unitFinal;

    // The drawer shows the LINE price. Seed it with the cart-JSON value and
    // let YMQ rewrite the marked span to the member line price. Both the
    // strikethrough and the live price use the *line* marker family so YMQ
    // rewrites them consistently with the per-unit price and the subtotal.
    var keyAttr = idx + '-' + itemKey;
    var priceHtml;
    if (lineOnSale) {
      priceHtml =
        '<s class="nb-cart__itemCompare">' + ymqItemSpan(keyAttr, 'ymq-b2b-item-original-line-price', compareCents) + '</s>' +
        ' <span class="nb-cart__itemFinal">' + ymqItemSpan(keyAttr, 'ymq-b2b-item-final-line-price', finalCents) + '</span>';
    } else {
      priceHtml =
        '<span class="nb-cart__itemFinal">' + ymqItemSpan(keyAttr, 'ymq-b2b-item-original-line-price', finalCents) + '</span>';
    }

    var checked = isChecked(itemKey);
    var wlImg = buildImageUrl(item.image, 400);
    var wlAttrs =
      ' data-nb-wishlist-move' +
      ' data-key="' + escapeHtml(itemKey) + '"' +
      ' data-product-id="' + escapeHtml(item.product_id || item.handle || itemKey) + '"' +
      ' data-product-handle="' + escapeHtml(item.handle || '') + '"' +
      ' data-product-title="' + escapeHtml(item.product_title || item.title || '') + '"' +
      ' data-product-image="' + escapeHtml(wlImg) + '"' +
      ' data-product-price="' + (unitFinal || 0) + '"' +
      ' data-product-url="' + escapeHtml(item.url || '') + '"';

    return (
      '<li class="nb-cart__item" data-nb-cart-line="' + line + '" data-key="' + escapeHtml(itemKey) + '" data-line-price="' + finalCents + '" data-quantity="' + item.quantity + '">' +
      '  <label class="nb-cart__itemSelect" aria-label="Include in checkout">' +
      '    <input type="checkbox" class="nb-cart__checkbox" data-nb-cart-checkbox data-key="' + escapeHtml(itemKey) + '"' + (checked ? ' checked' : '') + '>' +
      '    <span class="nb-cart__checkboxBox" aria-hidden="true">' +
      '      <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="2.5,8.5 6.5,12.5 13.5,4.5"/></svg>' +
      '    </span>' +
      '  </label>' +
      '  <a href="' + escapeHtml(item.url) + '" class="nb-cart__itemImg" tabindex="-1" aria-hidden="true">' +
      (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy" width="120" height="120">' : '') +
      '  </a>' +
      '  <div class="nb-cart__itemBody">' +
      '    <a href="' + escapeHtml(item.url) + '" class="nb-cart__itemName">' + escapeHtml(item.product_title || item.title) + '</a>' +
      (variantText ? '    <div class="nb-cart__itemVariant">' + escapeHtml(variantText) + '</div>' : '') +
      '    <div class="nb-cart__itemPrice">' + priceHtml + '</div>' +
      '  </div>' +
      '  <div class="nb-cart__itemAside">' +
      '    <div class="nb-cart__itemActions">' +
      '      <button type="button" class="nb-cart__itemIconBtn nb-cart__itemRemove" data-nb-remove data-key="' + escapeHtml(itemKey) + '" aria-label="Remove item" title="Remove">' +
      '        <svg viewBox="0 0 18 18" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 5h12M7 5V3h4v2M5.5 5l.7 10h5.6L12.5 5M7.5 8.5v4M10.5 8.5v4"/></svg>' +
      '      </button>' +
      '      <button type="button" class="nb-cart__itemIconBtn nb-cart__itemWishlist"' + wlAttrs + ' aria-label="Move to wishlist" title="Move to wishlist">' +
      '        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
      '      </button>' +
      '    </div>' +
      '    <div class="nb-cart__qty">' +
      '      <button type="button" class="nb-cart__qtyBtn" data-nb-qty-minus data-key="' + escapeHtml(itemKey) + '" aria-label="Decrease">−</button>' +
      '      <input type="number" class="nb-cart__qtyInput" data-nb-qty-input data-key="' + escapeHtml(itemKey) + '" value="' + item.quantity + '" min="0" inputmode="numeric" aria-label="Quantity">' +
      '      <button type="button" class="nb-cart__qtyBtn" data-nb-qty-plus data-key="' + escapeHtml(itemKey) + '" aria-label="Increase">+</button>' +
      '    </div>' +
      '  </div>' +
      '</li>'
    );
  }

  // Cart-page row renderer: a 4-column grid (Product | Price | Qty | Total).
  // The drawer keeps using renderItem() above for its compact card style.
  function renderItemPageRow(item, line) {
    var img = buildImageUrl(item.image, 240);
    var variantBits = [];
    if (!item.product_has_only_default_variant && item.options_with_values) {
      item.options_with_values.forEach(function (o) { variantBits.push(o.name + ': ' + o.value); });
    }
    var variantText = variantBits.join(' · ');

    var itemKey = item.key || '';
    var idx = line - 1;
    var keyAttr = idx + '-' + itemKey;
    var unitOriginal = item.original_price || item.price || 0;
    var unitFinal = item.final_price || item.price || 0;
    var unitOnSale = unitOriginal > 0 && unitFinal > 0 && unitOriginal !== unitFinal;

    // Seed with the cart-JSON ORIGINAL price and let YMQ rewrite the marked
    // span to the customer's member price (see ymqItemSpan). The per-unit
    // PRICE column and the TOTAL column MUST use the matching marker family:
    // when there's no native sale (the usual case — the discount is entirely
    // YMQ-driven) the per-unit uses ymq-b2b-item-original-price and the line
    // total uses ymq-b2b-item-original-line-price. Mixing -original-* with
    // -final-* (the previous bug) made YMQ write inconsistent values, so the
    // per-unit showed one discount and the line total/subtotal another.
    var priceHtml = unitOnSale
      ? '<span class="nb-cartpage__rowCompare"><s>' + ymqItemSpan(keyAttr, 'ymq-b2b-item-original-price', unitOriginal) + '</s></span>' +
        ymqItemSpan(keyAttr, 'ymq-b2b-item-final-price', unitFinal)
      : ymqItemSpan(keyAttr, 'ymq-b2b-item-original-price', unitFinal);

    var lineOriginal = unitOriginal * item.quantity;
    var lineFinal = unitFinal * item.quantity;
    var lineTotalHtml = unitOnSale
      ? '<span class="nb-cartpage__rowCompare"><s>' + ymqItemSpan(keyAttr, 'ymq-b2b-item-original-line-price', lineOriginal) + '</s></span> ' +
        ymqItemSpan(keyAttr, 'ymq-b2b-item-final-line-price', lineFinal)
      : ymqItemSpan(keyAttr, 'ymq-b2b-item-original-line-price', lineFinal);

    var checked = isChecked(itemKey);
    var wlImg = buildImageUrl(item.image, 400);
    var wlAttrs =
      ' data-nb-wishlist-move' +
      ' data-key="' + escapeHtml(itemKey) + '"' +
      ' data-product-id="' + escapeHtml(item.product_id || item.handle || itemKey) + '"' +
      ' data-product-handle="' + escapeHtml(item.handle || '') + '"' +
      ' data-product-title="' + escapeHtml(item.product_title || item.title || '') + '"' +
      ' data-product-image="' + escapeHtml(wlImg) + '"' +
      ' data-product-price="' + (unitFinal || 0) + '"' +
      ' data-product-url="' + escapeHtml(item.url || '') + '"';

    return (
      '<li class="nb-cartpage__row" data-nb-cart-line="' + line + '" data-key="' + escapeHtml(itemKey) + '">' +
      '  <label class="nb-cartpage__rowSelect" aria-label="Include in checkout">' +
      '    <input type="checkbox" class="nb-cartpage__checkbox" data-nb-cart-checkbox data-key="' + escapeHtml(itemKey) + '"' + (checked ? ' checked' : '') + '>' +
      '    <span class="nb-cartpage__checkboxBox" aria-hidden="true">' +
      '      <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="2.5,8.5 6.5,12.5 13.5,4.5"/></svg>' +
      '    </span>' +
      '  </label>' +
      '  <a href="' + escapeHtml(item.url) + '" class="nb-cartpage__rowImg" tabindex="-1" aria-hidden="true">' +
      (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy" width="160" height="160">' : '') +
      '  </a>' +
      '  <div class="nb-cartpage__rowBody">' +
      '    <a href="' + escapeHtml(item.url) + '" class="nb-cartpage__rowName">' + escapeHtml(item.product_title || item.title) + '</a>' +
      (variantText ? '    <div class="nb-cartpage__rowVariant">' + escapeHtml(variantText) + '</div>' : '') +
      '    <div class="nb-cartpage__rowLineTotalMobile">' + lineTotalHtml + '</div>' +
      '  </div>' +
      '  <div class="nb-cartpage__rowPrice">' + priceHtml + '</div>' +
      '  <div class="nb-cartpage__rowQty">' +
      '    <div class="nb-cartpage__qtyPill">' +
      '      <button type="button" class="nb-cartpage__qtyBtn" data-nb-qty-minus data-key="' + escapeHtml(itemKey) + '" aria-label="Decrease">−</button>' +
      '      <input type="number" class="nb-cartpage__qtyInput" data-nb-qty-input data-key="' + escapeHtml(itemKey) + '" value="' + item.quantity + '" min="0" inputmode="numeric" aria-label="Quantity">' +
      '      <button type="button" class="nb-cartpage__qtyBtn" data-nb-qty-plus data-key="' + escapeHtml(itemKey) + '" aria-label="Increase">+</button>' +
      '    </div>' +
      '  </div>' +
      '  <div class="nb-cartpage__rowTotal">' + lineTotalHtml + '</div>' +
      '  <div class="nb-cartpage__rowActions">' +
      '    <button type="button" class="nb-cartpage__rowIconBtn nb-cartpage__rowRemove" data-nb-remove data-key="' + escapeHtml(itemKey) + '" aria-label="Remove item" title="Remove">' +
      '      <svg viewBox="0 0 18 18" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 5h12M7 5V3h4v2M5.5 5l.7 10h5.6L12.5 5M7.5 8.5v4M10.5 8.5v4"/></svg>' +
      '    </button>' +
      '    <button type="button" class="nb-cartpage__rowIconBtn nb-cartpage__rowWishlist"' + wlAttrs + ' aria-label="Move to wishlist" title="Move to wishlist">' +
      '      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
      '    </button>' +
      '  </div>' +
      '</li>'
    );
  }

  function renderTotal(cents) {
    // Intentionally NO ymq-b2b-cart-total-price marker here: the third-party
    // YMQ B2B app hooks that attribute and rewrites the text via its own
    // recompute, which has been observed to zero out the subtotal in
    // non-B2B / mixed states. We compute the total ourselves from line
    // prices and own this DOM node.
    return formatMoney(cents);
  }

  // ---------- member (YMQ B2B) subtotal reconciliation ----------
  // For logged-in members the YMQ B2B app rewrites each line's displayed
  // price to the member price AFTER we render, but it never touches our
  // self-owned subtotal node (we deliberately omit its marker — see
  // renderTotal). The cart JSON we compute the subtotal from still carries
  // the non-member price, so the subtotal would otherwise show the original
  // total while the line items show member prices. To stay in sync we sum
  // the line totals actually displayed in the DOM for the selected lines.
  //
  // Returns null when the displayed line totals can't be fully resolved yet
  // (e.g. YMQ hasn't run) so the caller keeps the cart-JSON fallback.
  function domSubtotalCents(root) {
    var rows = root.querySelectorAll('[data-nb-cart-line]');
    if (!rows.length) return null;
    var sum = 0;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var key = row.getAttribute('data-key');
      if (key && !isChecked(key)) continue;
      var span = row.querySelector('[ymq-b2b-item-final-line-price]')
              || row.querySelector('[ymq-b2b-item-original-line-price]');
      var cents = span ? parseMoneyToCents(span.textContent) : null;
      if (cents == null) return null; // incomplete — keep the fallback total
      sum += cents;
    }
    return sum;
  }

  // Recompute the subtotal from the displayed (possibly YMQ-rewritten) line
  // totals, falling back to the cart-JSON total when they aren't available.
  function reconcileMemberSubtotal(root, totalValEl, fallbackCents) {
    if (!totalValEl) return;
    var domCents = domSubtotalCents(root);
    totalValEl.innerHTML = formatMoney(domCents == null ? fallbackCents : domCents);
  }

  // Watch the line-items container for YMQ price rewrites and re-sync the
  // subtotal once they land. Attached once per container; the container node
  // itself persists across our re-renders (we only swap its innerHTML), so a
  // single observer keeps working. The subtotal node lives outside this
  // container, so updating it never re-triggers the observer.
  function watchMemberPrices(root, itemsEl, totalValEl) {
    if (!itemsEl || !totalValEl || itemsEl.__nbYmqObserved) return;
    if (typeof MutationObserver === 'undefined') return;
    itemsEl.__nbYmqObserved = true;
    var timer = null;
    var obs = new MutationObserver(function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        var domCents = domSubtotalCents(root);
        if (domCents != null) totalValEl.innerHTML = formatMoney(domCents);
      }, 120);
    });
    obs.observe(itemsEl, { childList: true, subtree: true, characterData: true });
  }

  function findItemByKey(key) {
    if (!state.cart) return null;
    for (var i = 0; i < state.cart.items.length; i++) {
      if (state.cart.items[i].key === key) return state.cart.items[i];
    }
    return null;
  }

  // ---------- optimistic updates ----------
  function setQuantityByKey(key, qty) {
    if (!state.cart) return;
    qty = Math.max(0, Math.floor(qty));
    var item = findItemByKey(key);
    if (!item) return;
    if (qty === item.quantity) return;

    state.desiredByKey[key] = qty;

    if (qty === 0) {
      var idx = state.cart.items.indexOf(item);
      if (idx >= 0) state.cart.items.splice(idx, 1);
    } else {
      item.quantity = qty;
      var unitOriginal = item.original_price || item.price || 0;
      var unitFinal = item.final_price || item.price || 0;
      item.original_line_price = unitOriginal * qty;
      item.line_price = unitOriginal * qty;
      item.final_line_price = unitFinal * qty;
    }
    renderAll();
    queueServerSync();
  }

  function queueServerSync() {
    if (state.syncTimer) clearTimeout(state.syncTimer);
    state.syncTimer = setTimeout(serverSync, 250);
  }

  function serverSync() {
    state.syncTimer = null;
    if (!state.cart) return;
    if (state.syncing) { state.pendingResync = true; return; }

    state.syncing = true;
    state.syncFailed = false;
    var desiredSnapshot = Object.assign({}, state.desiredByKey);
    var prevServerQty = {};

    fetch(CART_GET, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (serverCart) {
        // Capture pre-update server quantities so we can distinguish
        // "user actively tried to add more" from "item was already at cap".
        serverCart.items.forEach(function (i) { prevServerQty[i.key] = i.quantity; });
        // Build positional updates from server's current line order. Items
        // not in our desired map keep their server quantity; items in our
        // desired map get the user's requested quantity (including 0).
        var serverUpdates = serverCart.items.map(function (i) {
          return desiredSnapshot.hasOwnProperty(i.key) ? desiredSnapshot[i.key] : i.quantity;
        });
        return fetch(CART_UPDATE, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: serverUpdates })
        }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); });
      })
      .then(function (res) {
        // Shopify returns HTTP 422 from /cart/update.js when a requested
        // quantity exceeds available inventory (rather than silently
        // capping). Surface that as the centered stock modal and re-sync
        // from /cart.js so the drawer / page reflects truth.
        if (!res.ok || res.data.status) {
          state.syncFailed = true;
          var msg = res.data.description || res.data.message
            || 'A quantity in your cart exceeds available stock.';
          Object.keys(desiredSnapshot).forEach(function (k) { delete state.desiredByKey[k]; });
          return fetchCart().then(function () {
            renderAll();
            showStockNotice(msg);
          });
        }
        var cart = res.data;
        // Stock cap detection. Only flag items where the user actively
        // requested MORE than what they previously had AND the server gave
        // back LESS than requested. This avoids false alarms when one item
        // is already at its stock ceiling and the user is editing other
        // items in the same debounce window.
        var capped = [];
        Object.keys(desiredSnapshot).forEach(function (key) {
          var wanted = desiredSnapshot[key];
          if (wanted === 0) return; // intended remove
          var wasAt = prevServerQty.hasOwnProperty(key) ? prevServerQty[key] : 0;
          if (wanted <= wasAt) return; // user didn't try to increase past current
          var serverItem = null;
          for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === key) { serverItem = cart.items[i]; break; }
          var got = serverItem ? serverItem.quantity : 0;
          if (got < wanted) capped.push({ name: serverItem ? (serverItem.product_title || serverItem.title) : 'item', got: got });
        });

        state.cart = cart;
        // Only clear the keys that were part of THIS sync. Any new clicks
        // that landed in state.desiredByKey while we were in flight stay
        // queued for the next sync.
        Object.keys(desiredSnapshot).forEach(function (k) { delete state.desiredByKey[k]; });
        renderAll();

        if (capped.length) {
          var capMsg = capped.length === 1
            ? 'Only ' + capped[0].got + ' of "' + capped[0].name + '" available. The quantity in your cart has been adjusted to the maximum we can ship.'
            : capped.length + ' items in your cart exceeded available stock and have been adjusted to the maximum we can ship.';
          showStockNotice(capMsg);
        }
      })
      .catch(function (e) {
        state.syncFailed = true;
        console.error('Cart sync failed', e);
        toast('Could not update cart. Please try again.');
        // Drop the snapshot keys so a stale desired qty (e.g. an accidental
        // + click on an already-maxed item) cannot bleed into the next sync
        // and trigger a false stock-cap warning for an unrelated edit.
        Object.keys(desiredSnapshot).forEach(function (k) { delete state.desiredByKey[k]; });
        fetchCart().then(renderAll);
      })
      .then(function () {
        state.syncing = false;
        if (state.pendingResync) { state.pendingResync = false; queueServerSync(); }
      });
  }

  // ---------- toast (small, transient) ----------
  function toast(msg) {
    var t = document.getElementById('nb-cart-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'nb-cart-toast';
      t.className = 'nb-cart-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._hideTimer);
    t._hideTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 3500);
  }

  // ---------- stock notice (centered modal, stays until dismissed) ----------
  function showStockNotice(msg) {
    var existing = document.getElementById('nb-cart-stock-notice');
    if (existing) existing.parentNode.removeChild(existing);

    var wrap = document.createElement('div');
    wrap.id = 'nb-cart-stock-notice';
    wrap.className = 'nb-cart-stock-notice';
    wrap.setAttribute('role', 'alertdialog');
    wrap.setAttribute('aria-live', 'assertive');
    wrap.innerHTML =
      '<div class="nb-cart-stock-notice__card">' +
      '  <div class="nb-cart-stock-notice__icon" aria-hidden="true">' +
      '    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16.5v.01"/></svg>' +
      '  </div>' +
      '  <div class="nb-cart-stock-notice__title">Out of stock</div>' +
      '  <div class="nb-cart-stock-notice__msg"></div>' +
      '  <button type="button" class="nb-cart-stock-notice__btn">Got it</button>' +
      '</div>';
    document.body.appendChild(wrap);
    wrap.querySelector('.nb-cart-stock-notice__msg').textContent = msg;

    requestAnimationFrame(function () { wrap.classList.add('is-visible'); });

    function close() {
      wrap.classList.remove('is-visible');
      setTimeout(function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); }, 240);
      document.removeEventListener('keyup', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    wrap.querySelector('.nb-cart-stock-notice__btn').addEventListener('click', close);
    wrap.addEventListener('click', function (e) { if (e.target === wrap) close(); });
    document.addEventListener('keyup', onKey);

    // Auto-dismiss after a generous window so the user has time to read.
    setTimeout(function () { if (wrap.parentNode) close(); }, 8000);
  }

  // ---------- events ----------
  function bindEvents() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      var plus = t.closest && t.closest('[data-nb-qty-plus]');
      var minus = t.closest && t.closest('[data-nb-qty-minus]');
      var remove = t.closest && t.closest('[data-nb-remove]');
      if (plus) {
        e.preventDefault();
        var key = plus.getAttribute('data-key');
        var it = findItemByKey(key);
        if (it) setQuantityByKey(key, it.quantity + 1);
      } else if (minus) {
        e.preventDefault();
        var key2 = minus.getAttribute('data-key');
        var it2 = findItemByKey(key2);
        if (it2) setQuantityByKey(key2, it2.quantity - 1);
      } else if (remove) {
        e.preventDefault();
        var key3 = remove.getAttribute('data-key');
        var li = remove.closest('.nb-cart__item, .nb-cartpage__row');
        if (li) {
          li.style.maxHeight = li.offsetHeight + 'px';
          requestAnimationFrame(function () {
            li.classList.add('is-removing');
            li.style.maxHeight = '0';
          });
        }
        setTimeout(function () { setQuantityByKey(key3, 0); }, 220);
      }
    });

    document.addEventListener('change', function (e) {
      var inp = e.target.closest && e.target.closest('[data-nb-qty-input]');
      if (inp) {
        var key = inp.getAttribute('data-key');
        var qty = parseInt(inp.value, 10);
        if (!Number.isFinite(qty)) qty = 0;
        setQuantityByKey(key, qty);
        return;
      }
      // Selection checkbox toggled — recompute totals without refetching cart.
      var cb = e.target.closest && e.target.closest('[data-nb-cart-checkbox]');
      if (cb) {
        setChecked(cb.getAttribute('data-key'), cb.checked);
        renderAll();
      }
    });

    // Resume the button click after cart updates so YMQ can create its
    // discounted checkout. A direct /checkout redirect bypasses the app.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-nb-cart-checkout]');
      if (!btn) return;
      if (btn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (!state.cart) return;
      var items = visibleItems(state.cart);
      var unchecked = items.filter(function (i) { return !isChecked(i.key); });
      if (!unchecked.length && !state.syncing && !state.syncTimer && !state.pendingResync) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      state.checkoutPending = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
      // Block YMQ's delegated click handler until selection and quantities
      // are saved; otherwise it can check out the old cart in parallel.
      unchecked.forEach(function (i) { setQuantityByKey(i.key, 0); });
      var attempts = 0;
      (function waitAndGo() {
        attempts += 1;
        if (!state.syncing && !state.syncTimer && !state.pendingResync) {
          state.checkoutPending = false;
          renderAll();
          if (state.syncFailed || visibleItems(state.cart).some(function (item) { return !isChecked(item.key); })) {
            toast('Could not update cart. Please try again.');
            return;
          }
          try {
            if (window.ymqB2b && typeof window.ymqB2b.isNeedCheckout === 'function') {
              window.ymqB2b.isNeedCheckout(state.cart);
            }
          } catch (err) {
            console.error('Member checkout preparation failed', err);
            toast('Could not prepare checkout. Please try again.');
            return;
          }
          btn.click();
          return;
        }
        if (attempts > 100) {
          state.checkoutPending = false;
          renderAll();
          toast('Cart is still updating. Please try again.');
          return;
        }
        setTimeout(waitAndGo, 100);
      })();
    }, true);

    // Catch-all: any <form action="/cart/add"> on the page gets routed
    // through NbCart.addAndOpen so the stock modal + drawer experience is
    // consistent everywhere (refills grid, related products, etc.) without
    // having to wire each section individually.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      // Skip forms that already have their own handler (preventDefault'd).
      if (e.defaultPrevented) return;
      if (form.hasAttribute('data-nb-cart-form-skip')) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      var fd = new FormData(form);
      var quiet = form.hasAttribute('data-nb-cart-quietadd');
      var btn = form.querySelector('button[type="submit"], [name="add"]');
      var origHtml = btn ? btn.innerHTML : '';
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        // Preserve the icon if present; just swap the text node.
        var textSpan = btn.querySelector('.nb-cup__cardBtnText');
        if (textSpan) textSpan.textContent = 'Adding…';
        else btn.textContent = 'Adding…';
      }
      var promise = quiet
        ? window.NbCart.addQuietly(fd)
        : window.NbCart.addAndOpen(fd);
      promise.then(function () {
        if (!btn) return;
        btn.classList.remove('is-loading');
        if (quiet) {
          // Brief "Added" confirmation, then collapse the card so the
          // customer doesn't re-add the same product.
          btn.classList.add('is-added');
          var ts = btn.querySelector('.nb-cup__cardBtnText');
          if (ts) ts.textContent = 'Added';
          else btn.textContent = 'Added';
          var card = form.closest('.nb-product-card--upsell, .nb-cup__card');
          setTimeout(function () {
            if (card) {
              card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
              card.style.opacity = '0';
              card.style.transform = 'scale(0.92)';
              setTimeout(function () { if (card.parentNode) card.parentNode.removeChild(card); }, 320);
            } else {
              btn.classList.remove('is-added');
              btn.innerHTML = origHtml;
              btn.disabled = false;
            }
          }, 900);
        } else {
          btn.innerHTML = origHtml;
          btn.disabled = false;
        }
      }, function () {
        if (!btn) return;
        btn.classList.remove('is-loading');
        btn.innerHTML = origHtml;
        btn.disabled = false;
      });
    });
  }

  // ---------- drawer ----------
  var Drawer = {
    el: null,
    overlay: null,
    init: function () {
      this.el = document.getElementById('nb-cart-drawer');
      this.overlay = document.getElementById('nb-cart-drawer-overlay');
      if (!this.el) return;
      var self = this;
      // NOTE: clicking the cart icon ([data-cart-link]) intentionally
      // does NOT open the drawer - it follows its href="/cart" so the
      // customer lands on the full cart page. The drawer is only
      // surfaced automatically after add-to-cart (NbCart.addAndOpen)
      // or by explicit JS API calls.
      this.el.querySelectorAll('[data-nb-cart-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.close(); });
      });
      // Click on the dark overlay closes the drawer.
      if (this.overlay) {
        this.overlay.addEventListener('click', function () { self.close(); });
        this.overlay.addEventListener('touchend', function (e) { e.preventDefault(); self.close(); }, { passive: false });
      }
      // Fallback: any click outside the drawer panel also closes (covers
      // any z-index quirk with overlay receiving the click).
      document.addEventListener('click', function (e) {
        if (!self.isOpen()) return;
        if (!self.el) return;
        if (self.el.contains(e.target)) return;
        // Ignore clicks that re-open the drawer (cart link).
        if (e.target.closest && e.target.closest('[data-cart-link]')) return;
        // Ignore clicks on the stock-notice modal / toast — those overlay
        // the page but should NOT count as "outside the drawer".
        if (e.target.closest && e.target.closest('#nb-cart-stock-notice')) return;
        if (e.target.closest && e.target.closest('#nb-cart-toast')) return;
        self.close();
      }, true);
      document.addEventListener('keyup', function (e) { if (e.key === 'Escape') self.close(); });
    },
    isOpen: function () { return !!(this.el && this.el.classList.contains('is-open')); },
    open: function () {
      if (!this.el) return;
      this.el.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.classList.add('nb-cart-open');
      document.dispatchEvent(new CustomEvent('niimbot:cart:opened'));
    },
    close: function () {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.classList.remove('nb-cart-open');
      document.dispatchEvent(new CustomEvent('niimbot:cart:closed'));
    },
  };

  // ---------- public API ----------
  window.NbCart = {
    open: function () { Drawer.open(); },
    close: function () { Drawer.close(); },
    refresh: function () { return fetchCart().then(renderAll); },
    addAndOpen: function (formData) {
      // Open the drawer immediately so the user gets instant feedback.
      Drawer.open();
      var drawerEl = document.getElementById('nb-cart-drawer');
      if (drawerEl) drawerEl.classList.add('is-loading');

      var addError = null;

      return fetch(CART_ADD, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: formData,
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) {
            addError = res.data.description || res.data.message || 'Could not add to cart';
          }
          // Always refresh from /cart.js — Shopify may have partially added
          // (e.g. capped to remaining stock) even when returning 422.
          return fetchCart();
        })
        .then(function () {
          renderAll();
          if (addError) showStockNotice(addError);
        })
        .catch(function (e) {
          console.error(e);
          // Try once more to get truth from the server.
          return fetchCart().then(renderAll).catch(function(){});
        })
        .then(function () {
          if (drawerEl) drawerEl.classList.remove('is-loading');
        });
    },
    // Add an item without opening the drawer; used by the cart-page
    // upsell rail. The cart page re-renders in place via renderAll so
    // the new line appears in the table and the shipping bar advances.
    addQuietly: function (formData) {
      var addError = null;
      return fetch(CART_ADD, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: formData,
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) {
            addError = res.data.description || res.data.message || 'Could not add to cart';
          }
          return fetchCart();
        })
        .then(function () {
          renderAll();
          if (addError) showStockNotice(addError);
        });
    },
  };

  function init() {
    var root = document.querySelector('[data-nb-cart]');
    if (root) state.isCustomer = root.getAttribute('data-customer') === 'true';
    Drawer.init();
    bindEvents();
    fetchCart().then(renderAll).catch(function () { /* ignore */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
